import { Suspense, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, Route, Routes } from "react-router-dom";

import { LanguageRedirect } from "@/components";
import { Layout } from "@/components/layout";
import { AppRoutes } from "@/routes";
import { SettingsUtils } from "@/utils";
import { lazyWithRetry as lazy } from "@/utils/lazyWithRetry";

import { useAuthRedirect, useNetworkStatus, useSnackbar } from "./hooks";
import { useClearStory } from "./hooks/useClearStory";

// Lazy load pages for code splitting (using named exports)
const About = lazy(() => import("@/pages/app/about"));
const EmailLinkLanding = lazy(() => import("@/pages/auth/email-link"));
const Home = lazy(() => import("@/pages/app/home"));
const Landing = lazy(() => import("@/pages/landing"));
const Library = lazy(() => import("@/pages/app/library"));
const Peek = lazy(() => import("@/pages/peek"));
const Privacy = lazy(() => import("@/pages/privacy"));
const Settings = lazy(() => import("@/pages/app/settings"));
const CatchAll = lazy(() => import("@/pages/catch-all"));
const Auth = lazy(() => import("@/pages/auth"));
const Stories = lazy(() => import("@/pages/app/stories"));
const Story = lazy(() => import("@/pages/app/story"));
const Terms = lazy(() => import("@/pages/terms"));

const App = () => {
  const { t } = useTranslation();

  useAuthRedirect();
  useClearStory();

  SettingsUtils.useLanguageSync();

  // Network status monitoring
  const { connected } = useNetworkStatus();
  const snackbar = useSnackbar();
  const wasOfflineRef = useRef(false);

  useEffect(() => {
    if (!connected && !wasOfflineRef.current) {
      // Just went offline
      snackbar.warning(t("common.offline"), {
        duration: 0, // Keep visible until dismissed or back online
      });
      wasOfflineRef.current = true;
    } else if (connected && wasOfflineRef.current) {
      // Just came back online
      snackbar.hideAll(); // Clear offline warning
      snackbar.success(t("common.online"));
      wasOfflineRef.current = false;
    }
  }, [connected, snackbar, t]);

  return (
    <Suspense fallback={<div style={{ minHeight: "100vh" }} />}>
      <Routes>
        <Route element={<Layout />}>
          <Route element={<CatchAll />} index path="*" />
          <Route path="/de" element={<LanguageRedirect />} />
          <Route path="/en" element={<LanguageRedirect />} />
          <Route path="/es" element={<LanguageRedirect />} />
          <Route path="/fr" element={<LanguageRedirect />} />
          <Route path="/it" element={<LanguageRedirect />} />
          <Route path="/pl" element={<LanguageRedirect />} />
          <Route path={AppRoutes.ABOUT()} element={<About />} />
          <Route path={AppRoutes.EMAIL_LINK()} element={<EmailLinkLanding />} />
          <Route path={AppRoutes.HOME()} element={<Home />} />
          <Route path={AppRoutes.LIBRARY()} element={<Library />} />
          <Route path={AppRoutes.PRIVACY()} element={<Privacy />} />
          <Route path={AppRoutes.ROOT()} element={<Landing />} index />
          <Route path={AppRoutes.SETTINGS()} element={<Settings />} />
          <Route path={AppRoutes.AUTH()} element={<Auth />} />
          <Route
            path={AppRoutes.SIGN_IN()}
            element={<Navigate to={AppRoutes.AUTH()} replace />}
          />
          <Route
            path={AppRoutes.SIGN_UP()}
            element={<Navigate to={AppRoutes.AUTH()} replace />}
          />
          <Route path={AppRoutes.STORIES()} element={<Stories />} />
          <Route path={AppRoutes.STORY(`:storyId`)} element={<Story />} />
          <Route path={AppRoutes.TERMS()} element={<Terms />} />
        </Route>
        <Route element={<Layout suppressNavElements />}>
          <Route path={AppRoutes.PEEK(`:storyId`)} element={<Peek />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default App;
